import { createUserScopedStorageAtom } from '@heidi/utils/jotai-storage';
import { atom, type SetStateAction } from 'jotai';

import { COMPLIANCE_NOTIFICATION_DISMISS_STORAGE_KEY } from '../../contracts';
import { type ComplianceNotificationDismissState } from './types';

/**
 * Bump when a field changes meaning (not when one is merely added — a missing
 * boolean already reads as `false`). A payload written under a different version
 * is dropped rather than merged, so a renamed or repurposed field can't be read
 * as the old one and silently un-dismiss, or permanently dismiss, a card.
 */
export const COMPLIANCE_NOTIFICATION_DISMISS_STATE_VERSION = 1;

const INITIAL_DISMISS_STATE: ComplianceNotificationDismissState = {
  version: COMPLIANCE_NOTIFICATION_DISMISS_STATE_VERSION,
  mfaSetup: false,
  inactivityLogout: false,
  mfaSetupRead: false,
  inactivityLogoutRead: false,
  mfaSetupFirstShownAt: null,
  inactivityLogoutFirstShownAt: null,
};

const persistedDismissAtom =
  createUserScopedStorageAtom<ComplianceNotificationDismissState>(
    COMPLIANCE_NOTIFICATION_DISMISS_STORAGE_KEY,
    INITIAL_DISMISS_STATE
  );

/**
 * Entries written before versioning carry no `version`, so they read as a
 * mismatch and start clean. The cost is one re-shown card per user at rollout,
 * which is the intended direction of failure: showing a compliance
 * recommendation again is recoverable, suppressing one on a stale payload is not.
 */
const readAtCurrentVersion = (
  stored: ComplianceNotificationDismissState
): ComplianceNotificationDismissState =>
  stored?.version === COMPLIANCE_NOTIFICATION_DISMISS_STATE_VERSION
    ? stored
    : INITIAL_DISMISS_STATE;

/**
 * Dismiss/read state for the pinned compliance cards.
 *
 * DEVICE-LOCAL, and the acceptance criteria say "for that user": this is
 * `localStorage` scoped by `user_ref_id`, so a dismissal does not follow the
 * user to another browser or machine. A server-side dismissal store is the fix;
 * until it exists the same user is re-shown a dismissed card on a new device.
 * Deliberate for this release (GRO-3989) — the alternative, not shipping
 * dismissal, leaves an unsuppressable pinned card.
 */
export const complianceNotificationDismissAtom = atom(
  (get) => readAtCurrentVersion(get(persistedDismissAtom)),
  (get, set, update: SetStateAction<ComplianceNotificationDismissState>) => {
    const current = readAtCurrentVersion(get(persistedDismissAtom));
    const next = typeof update === 'function' ? update(current) : update;
    // Stamped on write, never taken from the caller — a state built by spreading
    // a pre-version payload would otherwise persist without one.
    set(persistedDismissAtom, {
      ...next,
      version: COMPLIANCE_NOTIFICATION_DISMISS_STATE_VERSION,
    });
  }
);
